import { midi } from '../midi/MidiBus';
import { noteOff, noteOn } from '../midi/generate';
import type { MidiEvent } from '../midi/types';

export const ARP_PATTERNS = {
  OFF: [],
  MAJOR: [0, 4, 7],
  MINOR: [0, 3, 7],
  SUS2: [0, 2, 8],
  SUS4: [0, 6, 8],
  OCTAVIUS: [0, 7, 12, 7],
  DORIAN_RUN: [0, 4, 7, 4, 0, -5, -9, -12],
  MAJOR_7: [0, 4, 7, 11],
  MINOR_7: [0, 3, 7, 10],
  OCTAVES: [0, 12, 0, -12],
  FIFTHS: [0, 7, 12, 19],
  CHROMATIC: [0, 1, 2, 3],
} as const;

export type ArpPatternName = keyof typeof ARP_PATTERNS;

/** Selection buttons 1-12 map onto these patterns, in this order. */
export const ARP_PATTERN_ORDER: ArpPatternName[] = [
  'OFF',
  'MAJOR',
  'MINOR',
  'SUS2',
  'SUS4',
  'OCTAVIUS',
  'DORIAN_RUN',
  'MAJOR_7',
  'MINOR_7',
  'OCTAVES',
  'FIFTHS',
  'CHROMATIC',
];

interface Voice {
  timer: ReturnType<typeof setInterval> | null;
  step: number;
  sounding: number | null;
}

/**
 * Turns each held note into a repeating semitone pattern.
 *
 * Notes are emitted onto the shared MIDI bus tagged `arpeggiator`, so a
 * processor drops the original note before it reaches the synth while the
 * arpeggiator is enabled.
 */
export class Arpeggiator {
  private voices = new Map<number, Voice>();
  pattern: readonly number[] = ARP_PATTERNS.OCTAVIUS;
  bpm = 300;

  private get stepMs(): number {
    return 60_000 / this.bpm;
  }

  noteOn(event: MidiEvent): void {
    if (this.pattern.length === 0 || this.voices.has(event.value)) return;

    const voice: Voice = { timer: null, step: 0, sounding: null };
    const advance = () => {
      if (voice.sounding !== null) {
        midi.send(noteOff(voice.sounding, 0, event.channel, 'arpeggiator'));
        voice.sounding = null;
      }
      const semitone = this.pattern[voice.step % this.pattern.length];
      voice.step++;
      const note = event.value + semitone;
      if (note < 0 || note > 127) return;
      voice.sounding = note;
      midi.send(noteOn(note, event.velocity, event.channel, 'arpeggiator'));
    };

    advance();
    voice.timer = setInterval(advance, this.stepMs);
    this.voices.set(event.value, voice);
  }

  noteOff(event: MidiEvent): void {
    const voice = this.voices.get(event.value);
    if (!voice) return;
    if (voice.timer !== null) clearInterval(voice.timer);
    if (voice.sounding !== null) {
      midi.send(noteOff(voice.sounding, 0, event.channel, 'arpeggiator'));
    }
    this.voices.delete(event.value);
  }

  stopAll(): void {
    for (const [note, voice] of this.voices) {
      if (voice.timer !== null) clearInterval(voice.timer);
      if (voice.sounding !== null) midi.send(noteOff(voice.sounding, 0, 1, 'arpeggiator'));
      this.voices.delete(note);
    }
  }
}

export const arpeggiator = new Arpeggiator();
